import { Box } from '@mui/material';
import { memo } from 'react';
import { useSelector } from 'react-redux';
import { selectFocusMode } from '../../store/slices/focus_mode';

type FocusModeGradientProps = {
  isVisible?: boolean;
};

/**
 * フォーカスモード用グラデーションコンポーネント
 *
 * フォーカスモード時に画面下部20%のエリアに徐々に暗くなるグラデーションを適用します。
 * これにより、ユーザーの視線を画面中央に集中させ、入力に集中できる環境を提供します。
 *
 * @param {FocusModeGradientProps} props - コンポーネントのプロパティ
 * @param {boolean} [props.isVisible] - 表示状態
 * @returns {ReactElement | null} フォーカスモード用グラデーションコンポーネント
 */
const FocusModeGradient = memo(({ isVisible = true }: FocusModeGradientProps) => {
  const isFocusMode = useSelector(selectFocusMode);

  // フォーカスモードが無効または非表示の場合は何も表示しない
  if (!isFocusMode || !isVisible) {
    return null;
  }

  // グラデーションの強度を動的に計算（フォーカスモードの状態に応じて）
  const gradientIntensity = isFocusMode ? 1 : 0;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '30vh', // 画面下部30%のエリア
        zIndex: 0.5, // 背景より上、UIより下
        pointerEvents: 'none', // マウスイベントを無効化
        background: `linear-gradient(to bottom, 
          transparent 0%, 
          rgba(0, 0, 0, ${0.1 * gradientIntensity}) 20%, 
          rgba(0, 0, 0, ${0.3 * gradientIntensity}) 50%, 
          rgba(0, 0, 0, ${0.6 * gradientIntensity}) 80%, 
          rgba(0, 0, 0, ${0.8 * gradientIntensity}) 100%
        )`,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: isFocusMode ? 1 : 0,
        transform: isFocusMode ? 'translateY(0)' : 'translateY(20px)',
        filter: isFocusMode ? 'blur(0px)' : 'blur(2px)',
        animation: isFocusMode ? 'subtleGradientPulse 20s ease-in-out infinite' : 'none',
        '@keyframes subtleGradientPulse': {
          '0%, 100%': {
            opacity: 0.95,
          },
          '50%': {
            opacity: 1,
          },
        },
      }}
    />
  );
});

FocusModeGradient.displayName = 'FocusModeGradient';

export default FocusModeGradient;
